import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { HttpError } from '@microsoft/signalr';

@Injectable({
  providedIn: 'root'
})
export class ImageService {
  private imageUpdates = new Subject<{barcode: string, orientation: number}>();
  public imageUpdates$ = this.imageUpdates.asObservable();
  private imageCache: Map<string, { safeUrl: SafeUrl; blobUrl: string }> = new Map();
  private imageRequests: Map<string, Observable<SafeUrl>> = new Map();
  private missingImageUrl: SafeUrl = this.sanitizer.bypassSecurityTrustUrl("/images/not-found.png");
  private readonly MAX_CACHE_SIZE = 1000; // Maximum number of images to cache
  private baseUrl: string;

  constructor(private http: HttpClient, private sanitizer: DomSanitizer) {
    this.baseUrl = window.location.origin;
  }

  isImageCached(barcode: string, orientation: number): boolean {
    const cacheKey = `${barcode}:${orientation}`;
    return this.imageCache.get(cacheKey) ? true : false;
  }

  getImageUrl(barcode: string, orientation: number, fallbackBarcode?: string): string {
    let url = `${this.baseUrl}/api/images/image?barcode=${barcode}&orientation=${orientation}`;
    if (fallbackBarcode) {
      url += `&fallbackBarcode=${fallbackBarcode}`;
    }
    return url;
  }

  getImage(barcode: string, orientation: number, fallbackBarcode?: string): Observable<SafeUrl> {
    const cacheKey = `${barcode}:${orientation}`;

    // Check cache first
    const cachedImage = this.imageCache.get(cacheKey);
    if (cachedImage) {
      return of(cachedImage.blobUrl);
    }

    // Check if there's an ongoing request
    const existingRequest = this.imageRequests.get(cacheKey);
    if (existingRequest) {
      return existingRequest;
    }

    // Create new request
    const imageUrl = this.getImageUrl(barcode, orientation, fallbackBarcode);
    
    const newRequest = this.getImageBlob(imageUrl).pipe(
      map(blob => this.createImageUrl(blob, cacheKey)),
      catchError(error => {
        console.error(`[ImageService] Failed to fetch image for ${cacheKey}:`, error);
        this.imageFailed(barcode, orientation);
        return of(this.missingImageUrl);
      }),
      finalize(() => {
        this.imageRequests.delete(cacheKey);
      }),
      shareReplay(100)
    );

    this.imageRequests.set(cacheKey, newRequest);
    return newRequest;
  }

  
  // Retrieves list of products that have missing images
  detectMissingImages(products: { barcode: string, orientation: number }[]): Observable<{ barcode: string, orientation: number }[]> {
    const url = `${this.baseUrl}/api/images/missing-images`;
    return this.http.post<{ barcode: string, orientation: number }[]>(url, products);
  }

  private getImageBlob(imageUrl: string): Observable<Blob> {
    return this.http.get(imageUrl, {
      observe: 'response',
      responseType: 'blob'
    }).pipe(
      map(response => {
        if (!response.ok) {
          throw new HttpError("Failed to get image", response.status);
        }
        if (response.body === null) {
          throw new HttpError("Did not receive image blob", response.status);
        }
        return response.body;
      })
    );
  }

  private createImageUrl(blob: Blob, cacheKey: string): SafeUrl {
    // Check cache size before adding new entry
    if (this.imageCache.size >= this.MAX_CACHE_SIZE) {
      this.removeOldestCacheEntry();
    }

    const blobUrl = URL.createObjectURL(blob);
    const safeUrl = this.sanitizer.bypassSecurityTrustUrl(blobUrl);
    
    this.imageCache.set(cacheKey, {
      safeUrl,
      blobUrl
    });

    return safeUrl;
  }

  private removeOldestCacheEntry(): void {
    const firstKey = this.imageCache.keys().next().value;
    if (firstKey) {
      const entry = this.imageCache.get(firstKey);
      if (entry) {
        URL.revokeObjectURL(entry.blobUrl);
      }
      this.imageCache.delete(firstKey);
    }
  }

  private imageFailed(barcode: string, orientation: number): void {
    console.warn(`No image for product ${barcode}, Orientation: ${orientation}`);
  }

  public invalidateCacheEntry(barcode: string, orientation: number): void {
    const cacheKey = `${barcode}:${orientation}`;
    const cachedImage = this.imageCache.get(cacheKey);
    if (cachedImage) {
      try {
        URL.revokeObjectURL(cachedImage.blobUrl);
      } catch (e) {
        console.error(`[ImageService] Error revoking URL for ${cacheKey}:`, e);
      }
      this.imageCache.delete(cacheKey);
    }
    this.imageRequests.delete(cacheKey);
    
    // Notify subscribers that this image has been updated
    this.imageUpdates.next({ barcode, orientation });
  }

  clearCache(): void {
    // Clear pending requests
    this.imageRequests.clear();
    
    // Clear the image cache and revoke blob URLs
    this.imageCache.forEach(({ blobUrl }) => {
      try {
        URL.revokeObjectURL(blobUrl);
      } catch (e) {
        console.error('[ImageService] Error revoking URL:', e);
      }
    });
    this.imageCache.clear();
  }
}
